import React, { useState } from 'react';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '../../i18n';
import { ChevronDownIcon } from '../icons';

const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  pl: 'Polski',
  en: 'English',
};

/** Dropdown that switches the UI language between Polish and English. */
export default function LanguageSelector() {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentLanguage = (
    SUPPORTED_LANGUAGES.includes(i18n.language as SupportedLanguage)
      ? i18n.language
      : 'pl'
  ) as SupportedLanguage;

  const handleSelect = (lang: SupportedLanguage) => {
    i18n.changeLanguage(lang);
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: 200,
          height: 48,
          pl: 2,
          pr: 1,
          background: 'transparent',
          border: `1px solid ${theme.app.border}`,
          borderRadius: `${theme.shape.borderRadius}px`,
          color: theme.app.foreground,
          cursor: 'pointer',
          font: 'inherit',
        })}
      >
        <Typography
          variant="body2"
          component="span"
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        >
          {t('header.languageLabel')}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          sx={{ flex: 1, textAlign: 'left', color: 'text.primary' }}
        >
          {LANGUAGE_NAMES[currentLanguage]}
        </Typography>
        <ChevronDownIcon />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { width: 200, mt: 0.5 } } }}
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <MenuItem
            key={lang}
            selected={lang === currentLanguage}
            onClick={() => handleSelect(lang)}
          >
            {LANGUAGE_NAMES[lang]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
